import { useEffect, useState } from 'react'

import getBrowser from '@core/utils/helpers/getBrowser'

const usePrintKeyPress = (handlePrint: () => void, loading: boolean) => {
    useEffect(() => {
        const handlePrintKeysPressed = (event: KeyboardEvent) => {
            if (event.key === 'p' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault()
                if (loading) return

                handlePrint()
            }
        }

        document.addEventListener('keydown', handlePrintKeysPressed)

        return () => {
            document.removeEventListener('keydown', handlePrintKeysPressed)
        }
    }, [handlePrint, loading])
}

export const usePrintBlob = () => {
    const [loading, setLoading] = useState(false)
    const { name: browserName } = getBrowser()

    const handleSafariPrint = (blob?: Blob) => {
        if (!blob) return

        const url = URL.createObjectURL(blob)

        setLoading(true)

        let cleaned = false
        const timers = [] as number[]

        const cleanup = () => {
            if (cleaned) return
            cleaned = true
            timers.forEach((t) => clearTimeout(t))
            try {
                URL.revokeObjectURL(url)
            } catch {
                /* ignore */
            }
            setLoading(false)
        }

        const win = window.open(url, '_blank')

        const tryPrint = () => {
            if (!win) return
            try {
                win.focus()
                win.print()
            } catch {
                /* ignore */
            }
        }
        timers.push(
            window.setTimeout(tryPrint, 300),
            window.setTimeout(tryPrint, 900),
            window.setTimeout(tryPrint, 1800)
        )

        window.addEventListener('beforeunload', cleanup, { once: true })
    }

    const handleAllBrowsersPrint = (blob?: Blob) => {
        const fileUrl = blob ? URL.createObjectURL(blob) : null
        if (!fileUrl) return

        const iframe = document.createElement('iframe')
        document.body.appendChild(iframe)
        iframe.style.display = 'none'

        iframe.src = fileUrl
        const cleanUp = () => {
            if (iframe.parentNode) {
                document.body.removeChild(iframe)
            }
            URL.revokeObjectURL(fileUrl)
        }

        const handleWindowFocus = () => {
            cleanUp()
            window.removeEventListener('focus', handleWindowFocus)
        }

        iframe.onload = () => {
            iframe.contentWindow?.focus()
            iframe.contentWindow?.print()
            window.addEventListener('focus', handleWindowFocus)
        }

        iframe.src = fileUrl
    }

    const handlePrint = (blob?: Blob) => {
        console.log('print', blob)
        if (!blob) {
            console.log('No file to print')
            return
        }
        setLoading(true)

        if (browserName === 'Safari') {
            handleSafariPrint(blob)
        } else {
            handleAllBrowsersPrint(blob)
        }

        setLoading(false)
    }

    usePrintKeyPress(handlePrint, loading)

    return {
        loading,
        handlePrint,
    }
}
